import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, retry } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';
import { FilterParam } from '../../dashboard/interface/agency-filter';
import { AgencyList, VwAgency } from '../../dashboard/interface/agency-list';
import { WaitlistRequest } from '../../dashboard/models/waitlist-request';
import { AgencyRequest } from '../interfaces/add-to-worklist';
import { VwCompleteWorklist } from '../interfaces/complete-worklist';
import { VwMdeWaitlist } from '../interfaces/mde-waitlist';
import { SearchGlobalWorklistRequest } from '../interfaces/search-global-worklist';
import { NotificationService, Type } from '@progress/kendo-angular-notification';
import { SelectEvent, FileInfo } from '@progress/kendo-angular-upload';
import { LoadingService } from 'src/app/services/loading.service';
import { ISubmitUdoPayload } from '../interfaces/submit-udo-payload';
import { EvaluatorProvider } from '../interfaces/evaluator-providers';
import {
  AssessmentContractResponse,
  RelatedServiceContractResponse,
  AgencyWorkHoursResponse,
  SchoolYearsAndTermsResponse,
  SchoolDetailsResponse,
  ProviderInformationResponse,
  AgencyLocationInfo,
  UploadDocumentResponse,
  AddWorkHoursRequest,
  AddWorkHoursResponse,
  ProviderScheduleResponse
} from '../interfaces/admin-service-types';
import { SaveAssessmentContractModel } from '../models/save-assesment-contract-model';
import { SaveRelatedServiceContractModel } from '../models/save-related-service-contract-model';

const NOTIFICATION_HIDE_DELAY = 5000;
const ANIMATION_DURATION = 200;
const RETRY_COUNT = 1;

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private notificationService: NotificationService,
    private loadingService: LoadingService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public getGobalWorklist(): Observable<VwCompleteWorklist[]> {
    return this.http
      .get<VwCompleteWorklist[]>(`${this.config.apiUrl}/MDE/Worklist`)
      .pipe(catchError(handleError));
  }

  public getMdeWaitlist(agencySk: string): Observable<VwMdeWaitlist[]> {
    return this.http
      .get<VwMdeWaitlist[]>(`${this.config.apiUrl}/MDE/Waitlist/` + agencySk)
      .pipe(catchError(handleError));
  }

  public getMdeAgency(): Observable<AgencyList[]> {
    return this.http
      .get<AgencyList[]>(`${this.config.apiUrl}/MDE/MDEAgencies`)
      .pipe(catchError(handleError));
  }

  public getMDEAgencyList(filterParam: FilterParam): Observable<VwAgency[]> {
    return this.http
      .post<VwAgency[]>(
        `${this.config.apiUrl}/MDE/GetMDEAgencyList`,
        filterParam
      )
      .pipe(catchError(handleError));
  }

  public saveMdeAgency(request: AgencyRequest): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.config.apiUrl}/MDE/AddAgencyDetailsPerLocation`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getAgencyDetails(studentSk: string): Observable<VwAgency[]> {
    return this.http
      .get<VwAgency[]>(
        `${this.config.apiUrl}/MDE/GetMDEListByStudentSk/` + studentSk
      )
      .pipe(catchError(handleError));
  }

  public getMdeAgencyByParentAgencySk(agencySk: string): Observable<AgencyList> {
    return this.http
      .get<AgencyList>(`${this.config.apiUrl}/MDE/MDEParentAgency/${agencySk}`)
      .pipe(catchError(handleError));
  }

  public getMdeAgencyByAgencySk(agencySk: string): Observable<AgencyList> {
    return this.http
      .get<AgencyList>(`${this.config.apiUrl}/MDE/MDEAgency/${agencySk}`)
      .pipe(catchError(handleError));
  }

  public addToWorkList(request: AgencyRequest[]): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/AddToWorklist`, request)
      .pipe(catchError(handleError));
  }

  public addToWaitlist(request: AgencyRequest): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/AddToWaitlist`, request)
      .pipe(catchError(handleError));
  }

  public removeFromWaitlist(request: AgencyRequest[]): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/RemoveFromWaitlist`, request)
      .pipe(catchError(handleError));
  }

  public removeFromWorklist(studentSk: string): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.config.apiUrl}/MDE/RemoveFromWorklist/${studentSk}`,
        null
      )
      .pipe(catchError(handleError));
  }

  public addToMDWaitlist(request: WaitlistRequest): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/AddToMDEWaitlist`, request)
      .pipe(catchError(handleError));
  }

  public getStudentSearchDetails(
    request: SearchGlobalWorklistRequest
  ): Observable<VwCompleteWorklist[]> {
    return this.http
      .post<VwCompleteWorklist[]>(`${this.config.apiUrl}/MDE/Worklist`, request)
      .pipe(catchError(handleError));
  }

  public getEvaluatorProvidersByAgency(agencySk: string): Observable<EvaluatorProvider[]> {
    return this.http
      .get<EvaluatorProvider[]>(
        `${this.config.apiUrl}/AgencyManagement/EvaluatorProvidersByAgency/${agencySk}`
      )
      .pipe(catchError(handleError));
  }
  public getAssessmentsContractList(agencySk: string): Observable<AssessmentContractResponse> {
    return this.http
      .get<AssessmentContractResponse>(
        `${this.config.apiUrl}/AgencyManagement/AgencyAssessmentContract/${agencySk}`
      )
      .pipe(retry(1), catchError(handleError));
  }

  public getAgencyRelatedServiceContract(agencySk: string): Observable<RelatedServiceContractResponse> {
    return this.http
      .get<RelatedServiceContractResponse>(
        `${this.config.apiUrl}/AgencyManagement/AgencyRelatedServiceContract/${agencySk}`
      )
      .pipe(retry(1), catchError(handleError));
  }

  public getAgencyWorkHours(
    providerSk: string,
    agencySk?: string,
    schoolYearId?: string,
    termId?: string
  ): Observable<AgencyWorkHoursResponse[]> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('providerSk', providerSk);
    if (agencySk) params.append('agencySk', agencySk);
    if (schoolYearId) params.append('schoolYearId', schoolYearId);
    if (termId) params.append('termId', termId);
    return this.http
      .get<AgencyWorkHoursResponse[]>(`${this.config.apiUrl}/Provider/GetProviderWorkDetails?${params}`)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getSchoolDetails(schoolId: string): Observable<SchoolDetailsResponse> {
    return this.http
      .get<SchoolDetailsResponse>(
        `${this.config.apiUrl}/Provider/GetSchoolDetails?schoolId=${schoolId}`
      )
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getSchoolYearsAndTerms(): Observable<SchoolYearsAndTermsResponse> {
    return this.http
      .get<SchoolYearsAndTermsResponse>(`${this.config.apiUrl}/Provider/GetSchoolYearsAndTerms`)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getProviderInformation(
    providerSk: string,
    agencySK?: string
  ): Observable<ProviderInformationResponse> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('providerSk', providerSk);
    if (agencySK) params.append('agencySk', agencySK);
    return this.http
      .get<ProviderInformationResponse>(`${this.config.apiUrl}/Provider/GetProviderAndAgencyInfo?${params}`)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addAgencyWorkHours(request: AddWorkHoursRequest[]): Observable<AddWorkHoursResponse> {
    return this.http
      .post<AddWorkHoursResponse>(`${this.config.apiUrl}/Provider`, request)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addAgency(data: SaveAssessmentContractModel): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/AgencyManagement/AddAgencyContract`, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addRelatedService(data: SaveRelatedServiceContractModel): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/AgencyManagement/AddRelatedService`, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public inactiveAssessments(data: string[]): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.config.apiUrl}/AgencyManagement/ActiveAgency`, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public inactiveRelatedServices(date: string, data: string[]): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.config.apiUrl}/AgencyManagement/ActiveRelatedAgency?Effectivedate=${date}`,
        data
      )
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }
  public getMDEAgencyListByAgencySk(agencySk: string): Observable<AgencyList[]> {
    return this.http
      .get<AgencyList[]>(`${this.config.apiUrl}/MDE/GetMDEAgencyList/${agencySk}`)
      .pipe(catchError(handleError));
  }
  public getAllAgencyLocations(agencySk: string): Observable<AgencyLocationInfo[]> {
    return this.http
      .get<AgencyLocationInfo[]>(`${this.config.apiUrl}/MDE/AllAgencyLocations/${agencySk}`)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }
  public getMDEAgencyPerLocation(AgencyLocationSK: string): Observable<AgencyList> {
    return this.http
      .get<AgencyList>(`${this.config.apiUrl}/MDE/MDEAgencyPerLocation/${AgencyLocationSK}`)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getProviderSchedule(providerSk: string, startDate: string, endDate: string): Observable<ProviderScheduleResponse> {
    return this.http
      .get<ProviderScheduleResponse>(
        `${this.config.apiUrl}/Provider/GetProviderSchedules?providerSk=${providerSk}&startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getStudentClanderDetails(studentSk: string, startDate: string, endDate: string): Observable<ProviderScheduleResponse> {
    return this.http
      .get<ProviderScheduleResponse>(
        `${this.config.studentApiUrl}/Student/GetStudentCalendar?studentSk=${studentSk}&startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public uploadDocument(formData: FormData): Observable<UploadDocumentResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      enctype: 'multipart/form-data',
      Accept: 'application/json',
    });
    return this.http
      .post<UploadDocumentResponse>(
        `${this.config.apiUrl}/AgencyManagement/UploadAwardContract`,
        formData,
        { headers }
      )
      .pipe(catchError(handleError));
  }

    public submitAgencyProvider(request: ISubmitUdoPayload): Observable<string> {
    return this.http
      .post<string>(`${this.config.apiUrl}/Provider/SubmitAgencyProviderUDO`, request)
      .pipe(retry(1), catchError(handleError));
  }

    public getUdoAgencyInfo(providerSk: string, agencySk: string): Observable<ISubmitUdoPayload> {
    return this.http
      .get<ISubmitUdoPayload>(
        `${this.config.apiUrl}/Provider/GetUdoAgencyProviderInfo/${providerSk}/${agencySk}`
      )
      .pipe(retry(1), catchError(handleError));
  }
   
  validateAndUploadFile(ev: SelectEvent): void {
    const file: FileInfo = ev.files[0];
    if (file) {
      const allowedExtensions: string[] = ['.xls', '.xlsx'];
      const fileExtension: string = file.name
        .slice(file.name.lastIndexOf('.'))
        .toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        this.showMessage(
          'Allowed file types: .xls, .xlsx', 'error'
        );
        return;
      } else {
          
      const formData: FormData = new FormData();
      formData.append('file', file.rawFile);
      formData.append('fileType', '2');
      this.loadingService.show();
      this.uploadDocument(formData).subscribe((resp: UploadDocumentResponse) => {
        this.loadingService.hide();
        if (resp?.status === false) {
          this.showMessage('File upload failed', 'error');
          return;
        }
          this.showMessage('File uploaded successfully', 'success');
      });
      }
    }
  }
    showMessage(message: string, type: Type['style'] ): void {
    if (message) {
      this.notificationService.show({
        content: message,
        hideAfter: NOTIFICATION_HIDE_DELAY,
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'slide', duration: ANIMATION_DURATION },
        type: { style: type, icon: true },
      });
    }
  }
}
